/**
 * Shared PHOTOSHOP-STYLE IMAGE EDITOR (_GLOBAL.md G7,
 * mockups/image-editor-photoshop.png).
 *
 * FROZEN SHARED COMPONENT — owned by the foundation-integrator. Built
 * ONCE; every image-bearing panel (characters, locations, assets,
 * storyboard frames, mood board, …) reuses it. Panels MUST NOT build
 * their own image editor.
 *
 * What it provides (matching the mockup):
 *   - optional reference-image upload ("Upload reference images to guide
 *     the edit") shown as thumbnails,
 *   - a canvas of the image with a MASK overlay you draw on directly,
 *   - a left tool rail: pen/brush, eraser, color swatch, brush-size
 *     slider (with px read-out), undo,
 *   - tip banner: "draw directly on the image to target specific areas",
 *   - a prompt box: "Describe how you want to modify this image…",
 *   - Apply (runs the gen-edit hook) + Revert (restores the prior image).
 *
 * Generation is STUBBED via `studioGeneration.runGeneration` unless the
 * panel passes a real `onEdit` hook. Panels wire their Edit button like:
 *
 *   import { openImageEditor } from "../shared";
 *
 *   editButton.addEventListener("click", () => {
 *     openImageEditor({
 *       image: character.referenceImage ?? "",
 *       title: `Edit ${character.name}`,
 *       onChange: (next) => store.updateCharacter(id, { referenceImage: next }),
 *       // onEdit defaults to the stub; pass a real gen hook when available.
 *     });
 *   });
 */

import "./shared.css";
import { openModal } from "./Modal";
import { runGeneration } from "./studioGeneration";

export interface ImageEditRequest {
  /** The current image being edited (data URL or remote url). */
  image: string;
  /** Reference images uploaded to guide the edit (data URLs). */
  referenceImages: string[];
  /** The mask the user drew, as a PNG data URL — or null if untouched. */
  mask: string | null;
  /** The user's free-text edit prompt. */
  prompt: string;
}

/**
 * The gen-edit seam. Resolve to a new image (data URL / url) or null to
 * keep the current image. Panels pass a real implementation when their
 * provider is wired; otherwise the default stub is used.
 */
export type ImageEditHook = (req: ImageEditRequest) => Promise<string | null>;

export interface ImageEditorOptions {
  /** Starting image (data URL / url). May be empty for a blank canvas. */
  image: string;
  /** Gen-edit hook. Defaults to the shared placeholder generator. */
  onEdit?: ImageEditHook;
  /** Fired whenever the committed image changes (apply or revert). */
  onChange?: (nextImage: string) => void;
  /** Prompt textarea placeholder. */
  promptPlaceholder?: string;
}

type Tool = "brush" | "eraser";

const DEFAULT_PLACEHOLDER =
  "Describe how you want to modify this image…";

/** The default (stubbed) gen-edit hook — returns a placeholder image. */
const defaultEditHook: ImageEditHook = async (req) => {
  const result = await runGeneration({
    kind: "image",
    prompt: req.prompt || "image edit",
    seed: `${req.prompt}:${req.image.slice(0, 32)}`,
  });
  return result.url;
};

export class ImageEditor {
  private readonly root: HTMLDivElement;
  private readonly imageEl: HTMLImageElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly promptInput: HTMLTextAreaElement;
  private readonly applyButton: HTMLButtonElement;
  private readonly revertButton: HTMLButtonElement;
  private readonly refStrip: HTMLDivElement;

  private readonly onEdit: ImageEditHook;
  private readonly onChange?: (nextImage: string) => void;

  private currentImage: string;
  private previousImage: string | null = null;
  private referenceImages: string[] = [];

  private tool: Tool = "brush";
  private color = "#ef4444";
  private brushSize = 10;
  private drawing = false;
  private maskTouched = false;
  /** Undo stack of mask ImageData snapshots. */
  private readonly undoStack: ImageData[] = [];
  private busy = false;

  constructor(options: ImageEditorOptions) {
    this.onEdit = options.onEdit ?? defaultEditHook;
    this.onChange = options.onChange;
    this.currentImage = options.image;

    this.root = document.createElement("div");
    this.root.className = "studio-image-editor";

    // ---- Reference images (optional) -------------------------------
    const refSection = document.createElement("div");
    refSection.className = "studio-ie-refs";
    const refLabel = document.createElement("div");
    refLabel.className = "studio-ie-section-label";
    refLabel.textContent = "Reference Images (Optional)";
    const refRow = document.createElement("div");
    refRow.className = "studio-ie-ref-row";

    const refUpload = document.createElement("button");
    refUpload.type = "button";
    refUpload.className = "studio-ie-ref-upload";
    refUpload.innerHTML =
      '<span class="studio-ie-ref-icon">⤴</span><span>Add images</span>';
    const refInput = document.createElement("input");
    refInput.type = "file";
    refInput.accept = "image/*";
    refInput.multiple = true;
    refInput.style.display = "none";
    refUpload.addEventListener("click", () => refInput.click());
    refInput.addEventListener("change", () => this.onRefFiles(refInput.files));

    this.refStrip = document.createElement("div");
    this.refStrip.className = "studio-ie-ref-thumbs";

    const refHint = document.createElement("span");
    refHint.className = "studio-ie-ref-hint";
    refHint.textContent = "Upload reference images to guide the edit.";

    refRow.appendChild(refUpload);
    refRow.appendChild(this.refStrip);
    refRow.appendChild(refHint);
    refSection.appendChild(refLabel);
    refSection.appendChild(refRow);
    refSection.appendChild(refInput);

    // ---- Stage: tool rail + canvas ---------------------------------
    const stage = document.createElement("div");
    stage.className = "studio-ie-stage";

    const rail = this.buildToolRail();

    const canvasWrap = document.createElement("div");
    canvasWrap.className = "studio-ie-canvas-wrap";

    this.imageEl = document.createElement("img");
    this.imageEl.className = "studio-ie-image";
    this.imageEl.alt = "Image being edited";
    if (this.currentImage) this.imageEl.src = this.currentImage;

    this.canvas = document.createElement("canvas");
    this.canvas.className = "studio-ie-canvas";
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("ImageEditor: 2D canvas context unavailable");
    this.ctx = ctx;
    this.wireCanvas();

    canvasWrap.appendChild(this.imageEl);
    canvasWrap.appendChild(this.canvas);
    stage.appendChild(rail);
    stage.appendChild(canvasWrap);

    // ---- Tip banner ------------------------------------------------
    const tip = document.createElement("div");
    tip.className = "studio-ie-tip";
    tip.textContent = "⚠ Tip: draw directly on the image to target specific areas.";

    // ---- Prompt + actions ------------------------------------------
    this.promptInput = document.createElement("textarea");
    this.promptInput.className = "studio-ie-prompt";
    this.promptInput.placeholder = options.promptPlaceholder ?? DEFAULT_PLACEHOLDER;
    this.promptInput.dataset.kokoAction = "image-edit-prompt";

    const actions = document.createElement("div");
    actions.className = "studio-ie-actions";

    this.revertButton = document.createElement("button");
    this.revertButton.type = "button";
    this.revertButton.className = "studio-button studio-ie-revert";
    this.revertButton.textContent = "Revert";
    this.revertButton.disabled = true;
    this.revertButton.dataset.kokoAction = "image-edit-revert";
    this.revertButton.addEventListener("click", () => this.revert());

    this.applyButton = document.createElement("button");
    this.applyButton.type = "button";
    this.applyButton.className = "studio-button is-primary studio-ie-apply";
    this.applyButton.textContent = "Apply edit";
    this.applyButton.dataset.kokoAction = "image-edit-apply";
    this.applyButton.addEventListener("click", () => void this.apply());

    actions.appendChild(this.revertButton);
    actions.appendChild(this.applyButton);

    this.root.appendChild(refSection);
    this.root.appendChild(stage);
    this.root.appendChild(tip);
    this.root.appendChild(this.promptInput);
    this.root.appendChild(actions);

    // Size the mask canvas to the rendered image once it loads.
    this.imageEl.addEventListener("load", () => this.resizeCanvasToImage());
    if (this.imageEl.complete && this.imageEl.naturalWidth > 0) {
      this.resizeCanvasToImage();
    }
  }

  getElement(): HTMLElement {
    return this.root;
  }

  /** The currently committed image (data URL / url). */
  getImage(): string {
    return this.currentImage;
  }

  destroy(): void {
    this.root.remove();
  }

  // ------------------------------------------------------------------
  // Tool rail
  // ------------------------------------------------------------------

  private buildToolRail(): HTMLDivElement {
    const rail = document.createElement("div");
    rail.className = "studio-ie-rail";

    const brushBtn = this.toolButton("✎", "Brush", "brush");
    const eraserBtn = this.toolButton("⌫", "Eraser", "eraser");

    const sep1 = document.createElement("div");
    sep1.className = "studio-ie-rail-sep";

    const swatch = document.createElement("input");
    swatch.type = "color";
    swatch.value = this.color;
    swatch.className = "studio-ie-swatch";
    swatch.title = "Brush color";
    swatch.addEventListener("input", () => {
      this.color = swatch.value;
    });

    const sep2 = document.createElement("div");
    sep2.className = "studio-ie-rail-sep";

    const sizeWrap = document.createElement("div");
    sizeWrap.className = "studio-ie-size-wrap";
    const sizeSlider = document.createElement("input");
    sizeSlider.type = "range";
    sizeSlider.min = "1";
    sizeSlider.max = "80";
    sizeSlider.value = String(this.brushSize);
    sizeSlider.className = "studio-ie-size";
    sizeSlider.title = "Brush size";
    const sizeLabel = document.createElement("span");
    sizeLabel.className = "studio-ie-brush-size";
    sizeLabel.textContent = `${this.brushSize}px`;
    sizeSlider.addEventListener("input", () => {
      this.brushSize = Number(sizeSlider.value);
      sizeLabel.textContent = `${this.brushSize}px`;
    });
    sizeWrap.appendChild(sizeSlider);
    sizeWrap.appendChild(sizeLabel);

    const sep3 = document.createElement("div");
    sep3.className = "studio-ie-rail-sep";

    const undoBtn = document.createElement("button");
    undoBtn.type = "button";
    undoBtn.className = "studio-ie-tool";
    undoBtn.title = "Undo";
    undoBtn.textContent = "↺";
    undoBtn.addEventListener("click", () => this.undo());

    rail.appendChild(brushBtn);
    rail.appendChild(eraserBtn);
    rail.appendChild(sep1);
    rail.appendChild(swatch);
    rail.appendChild(sep2);
    rail.appendChild(sizeWrap);
    rail.appendChild(sep3);
    rail.appendChild(undoBtn);
    return rail;
  }

  private toolButton(glyph: string, title: string, tool: Tool): HTMLButtonElement {
    const button = document.createElement("button");
    button.type = "button";
    button.className = "studio-ie-tool";
    button.title = title;
    button.textContent = glyph;
    button.dataset.tool = tool;
    if (tool === this.tool) button.classList.add("is-selected");
    button.addEventListener("click", () => {
      this.tool = tool;
      this.root
        .querySelectorAll<HTMLButtonElement>(".studio-ie-tool[data-tool]")
        .forEach((b) => b.classList.toggle("is-selected", b.dataset.tool === tool));
    });
    return button;
  }

  // ------------------------------------------------------------------
  // Reference images
  // ------------------------------------------------------------------

  private onRefFiles(files: FileList | null): void {
    if (!files) return;
    for (const file of Array.from(files)) {
      const reader = new FileReader();
      reader.onload = () => {
        const url = String(reader.result);
        this.referenceImages.push(url);
        const thumb = document.createElement("img");
        thumb.className = "studio-ie-ref-thumb";
        thumb.src = url;
        thumb.alt = "Reference";
        this.refStrip.appendChild(thumb);
      };
      reader.readAsDataURL(file);
    }
  }

  // ------------------------------------------------------------------
  // Mask canvas
  // ------------------------------------------------------------------

  private resizeCanvasToImage(): void {
    const rect = this.imageEl.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width || this.imageEl.naturalWidth));
    const h = Math.max(1, Math.round(rect.height || this.imageEl.naturalHeight));
    if (this.canvas.width === w && this.canvas.height === h) return;
    this.canvas.width = w;
    this.canvas.height = h;
  }

  private wireCanvas(): void {
    const pos = (event: PointerEvent): { x: number; y: number } => {
      const rect = this.canvas.getBoundingClientRect();
      return {
        x: ((event.clientX - rect.left) / rect.width) * this.canvas.width,
        y: ((event.clientY - rect.top) / rect.height) * this.canvas.height,
      };
    };
    const start = (event: PointerEvent): void => {
      this.resizeCanvasToImage();
      this.pushUndo();
      this.drawing = true;
      this.maskTouched = true;
      this.canvas.setPointerCapture(event.pointerId);
      this.stroke(pos(event));
    };
    const move = (event: PointerEvent): void => {
      if (!this.drawing) return;
      this.stroke(pos(event));
    };
    const end = (event: PointerEvent): void => {
      this.drawing = false;
      try {
        this.canvas.releasePointerCapture(event.pointerId);
      } catch {
        // capture may already be released — ignore.
      }
    };
    this.canvas.addEventListener("pointerdown", start);
    this.canvas.addEventListener("pointermove", move);
    this.canvas.addEventListener("pointerup", end);
    this.canvas.addEventListener("pointerleave", end);
  }

  private stroke(point: { x: number; y: number }): void {
    this.ctx.globalCompositeOperation =
      this.tool === "eraser" ? "destination-out" : "source-over";
    this.ctx.fillStyle = this.color;
    this.ctx.beginPath();
    this.ctx.arc(point.x, point.y, this.brushSize / 2, 0, Math.PI * 2);
    this.ctx.fill();
  }

  private pushUndo(): void {
    if (this.canvas.width === 0 || this.canvas.height === 0) return;
    this.undoStack.push(
      this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height),
    );
    if (this.undoStack.length > 30) this.undoStack.shift();
  }

  private undo(): void {
    const prev = this.undoStack.pop();
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (prev) {
      this.ctx.globalCompositeOperation = "source-over";
      this.ctx.putImageData(prev, 0, 0);
    } else {
      this.maskTouched = false;
    }
  }

  private exportMask(): string | null {
    if (!this.maskTouched) return null;
    return this.canvas.toDataURL("image/png");
  }

  private clearMask(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.undoStack.length = 0;
    this.maskTouched = false;
  }

  // ------------------------------------------------------------------
  // Apply / Revert
  // ------------------------------------------------------------------

  private async apply(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    this.applyButton.disabled = true;
    this.applyButton.textContent = "Applying…";
    this.root.dataset.busy = "true";
    try {
      const next = await this.onEdit({
        image: this.currentImage,
        referenceImages: [...this.referenceImages],
        mask: this.exportMask(),
        prompt: this.promptInput.value.trim(),
      });
      if (next) {
        this.previousImage = this.currentImage;
        this.currentImage = next;
        this.imageEl.src = next;
        this.clearMask();
        this.revertButton.disabled = false;
        this.onChange?.(next);
      }
    } catch (error) {
      console.warn("[studio] image edit failed:", error);
    } finally {
      this.busy = false;
      this.applyButton.disabled = false;
      this.applyButton.textContent = "Apply edit";
      this.root.dataset.busy = "false";
    }
  }

  private revert(): void {
    if (this.previousImage === null) return;
    this.currentImage = this.previousImage;
    this.imageEl.src = this.currentImage;
    this.previousImage = null;
    this.revertButton.disabled = true;
    this.clearMask();
    this.onChange?.(this.currentImage);
  }
}

export interface OpenImageEditorOptions extends ImageEditorOptions {
  /** Modal title; defaults to "Edit image". */
  title?: string;
  /** Fired with the final committed image when the modal closes. */
  onDone?: (finalImage: string) => void;
}

/** Open the shared image editor in a modal. Returns a close fn. */
export function openImageEditor(options: OpenImageEditorOptions): () => void {
  const editor = new ImageEditor(options);
  const modal = openModal({
    title: options.title ?? "Edit image",
    variant: "is-image-editor",
    surface: "image-editor",
    onClose: () => {
      options.onDone?.(editor.getImage());
      editor.destroy();
    },
  });
  modal.body.appendChild(editor.getElement());
  return modal.close;
}
